import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { useStore } from "../context/StoreContext";
import api, { formatApiError } from "../lib/api";

export default function AdminSettings() {
  const { refreshBootstrap } = useStore();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setForm({
        ...r.data,
        shipping_flat_brl: ((r.data.shipping_flat || 0) / 100).toFixed(2).replace(".", ","),
        shipping_free_above_brl: ((r.data.shipping_free_above || 0) / 100).toFixed(2).replace(".", ","),
      });
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const parseBrl = (s) => Math.round(parseFloat(String(s || "0").replace(",", ".")) * 100) || 0;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        store_name: form.store_name,
        infinitepay_handle: form.infinitepay_handle || "",
        infinitepay_mode: form.infinitepay_mode,
        shipping_flat: parseBrl(form.shipping_flat_brl),
        shipping_free_above: parseBrl(form.shipping_free_above_brl),
        warranty_months: parseInt(form.warranty_months) || 12,
        card_fixed_text: form.card_fixed_text || "",
        card_adjust_x: form.card_adjust_x || 0,
        card_adjust_y: form.card_adjust_y || 0,
        card_adjust_scale: form.card_adjust_scale || 1,
        card_price_dy: form.card_price_dy || 0,
        card_price_dx: form.card_price_dx || 0,
        card_price_scale: form.card_price_scale || 1,
        card_price_color: form.card_price_color || "#241600",
        card_price_width: form.card_price_width || 52,
        card_price_height: form.card_price_height || 6,
        card_price_radius: form.card_price_radius ?? 2,
      });
      await refreshBootstrap();
      toast.success("Configurações salvas");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div data-testid="admin-settings">
      <h1 className="font-display text-3xl tracking-tight mb-10">Configurações</h1>
      <form onSubmit={save} className="max-w-2xl space-y-12">
        <section>
          <p className="label-caps mb-5">Loja</p>
          <div>
            <Label>Nome da loja</Label>
            <Input value={form.store_name || ""} onChange={(e) => set("store_name", e.target.value)} data-testid="setting-store-name"
              className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
          </div>
        </section>

        <section>
          <p className="label-caps mb-5">Pagamentos — InfinitePay</p>
          <div className="space-y-5 border border-white/10 rounded-lg p-6 bg-[#111111]">
            <div>
              <Label>Modo</Label>
              <Select value={form.infinitepay_mode} onValueChange={(v) => set("infinitepay_mode", v)}>
                <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="setting-pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  <SelectItem value="simulation">Simulação (teste sem cobrança)</SelectItem>
                  <SelectItem value="live">Produção (InfinitePay real)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>InfiniteTag / handle (sem o $)</Label>
              <Input value={form.infinitepay_handle || ""} onChange={(e) => set("infinitepay_handle", e.target.value)} data-testid="setting-infinitepay-handle"
                placeholder="sua-infinite-tag" className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500 font-mono" />
              <p className="text-[11px] text-zinc-600 mt-2">
                Habilite em Vendas → Checkout → Checkout Integrado no app InfinitePay. O webhook é registrado automaticamente como{" "}
                <span className="font-mono text-zinc-500">/api/payments/infinitepay/webhook</span> ao criar cada checkout.
              </p>
            </div>
            <div>
              <Label>Garantia dos produtos (meses)</Label>
              <Input type="number" min="1" value={form.warranty_months} onChange={(e) => set("warranty_months", e.target.value)} data-testid="setting-warranty"
                className="mt-1.5 bg-[#141414] border-white/10 w-32" />
            </div>
          </div>
        </section>

        <section>
          <p className="label-caps mb-5">Frete manual</p>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label>Frete fixo (R$)</Label>
              <Input value={form.shipping_flat_brl} onChange={(e) => set("shipping_flat_brl", e.target.value)} data-testid="setting-shipping-flat"
                className="mt-1.5 bg-[#141414] border-white/10" />
            </div>
            <div>
              <Label>Grátis acima de (R$, 0 = desativado)</Label>
              <Input value={form.shipping_free_above_brl} onChange={(e) => set("shipping_free_above_brl", e.target.value)} data-testid="setting-shipping-free"
                className="mt-1.5 bg-[#141414] border-white/10" />
            </div>
          </div>
          <p className="text-[11px] text-zinc-600 mt-3">Estrutura pronta para integração futura com Correios/transportadora.</p>
        </section>

        <section>
          <p className="label-caps mb-5">Cards de produto (global)</p>
          <div className="space-y-5 border border-white/10 rounded-lg p-6 bg-[#111111]">
            <div>
              <Label>Texto fixo exibido em todos os cards</Label>
              <Input value={form.card_fixed_text || ""} onChange={(e) => set("card_fixed_text", e.target.value)} data-testid="setting-card-text"
                placeholder="Ex.: Certificado de autenticidade incluso" className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
              <p className="text-[11px] text-zinc-600 mt-2">Pode ser sobrescrito por produto (campo "Texto fixo do card" na edição do produto).</p>
            </div>
            <p className="text-xs text-zinc-400 pt-2">Posição global da imagem dentro do frame (vale para produtos sem ajuste próprio):</p>
            <div>
              <Label className="text-xs">Zoom global: {form.card_adjust_scale || 1}x</Label>
              <Slider value={[form.card_adjust_scale || 1]} min={0.5} max={2.5} step={0.05} className="mt-3" data-testid="setting-card-scale"
                onValueChange={([v]) => set("card_adjust_scale", v)} />
            </div>
            <div>
              <Label className="text-xs">Mover horizontal: {form.card_adjust_x || 0}%</Label>
              <Slider value={[form.card_adjust_x || 0]} min={-50} max={50} step={1} className="mt-3" data-testid="setting-card-x"
                onValueChange={([v]) => set("card_adjust_x", v)} />
            </div>
            <div>
              <Label className="text-xs">Mover vertical: {form.card_adjust_y || 0}%</Label>
              <Slider value={[form.card_adjust_y || 0]} min={-50} max={50} step={1} className="mt-3" data-testid="setting-card-y"
                onValueChange={([v]) => set("card_adjust_y", v)} />
            </div>
            <p className="text-xs text-zinc-400 pt-2">Preço na placa dourada:</p>
            <div>
              <Label className="text-xs">Posição vertical do preço: {form.card_price_dy || 0}%</Label>
              <Slider value={[form.card_price_dy || 0]} min={-12} max={12} step={0.5} className="mt-3" data-testid="setting-price-dy"
                onValueChange={([v]) => set("card_price_dy", v)} />
            </div>
            <div>
              <Label className="text-xs">Posição horizontal do preço: {form.card_price_dx || 0}%</Label>
              <Slider value={[form.card_price_dx || 0]} min={-20} max={20} step={0.5} className="mt-3" data-testid="setting-price-dx"
                onValueChange={([v]) => set("card_price_dx", v)} />
            </div>
            <div>
              <Label className="text-xs">Tamanho do preço: {form.card_price_scale || 1}x</Label>
              <Slider value={[form.card_price_scale || 1]} min={0.7} max={1.6} step={0.05} className="mt-3" data-testid="setting-price-scale"
                onValueChange={([v]) => set("card_price_scale", v)} />
            </div>
            <div>
              <Label className="text-xs">Largura do fundo do preço: {form.card_price_width || 52}%</Label>
              <Slider value={[form.card_price_width || 52]} min={24} max={60} step={0.5} className="mt-3" data-testid="setting-price-width"
                onValueChange={([v]) => set("card_price_width", v)} />
            </div>
            <div>
              <Label className="text-xs">Altura do fundo do preço: {form.card_price_height || 6}%</Label>
              <Slider value={[form.card_price_height || 6]} min={3} max={14} step={0.5} className="mt-3" data-testid="setting-price-height"
                onValueChange={([v]) => set("card_price_height", v)} />
            </div>
            <div>
              <Label className="text-xs">Cor do preço</Label>
              <div className="flex gap-2 mt-1.5 items-center">
                <input type="color" value={form.card_price_color || "#241600"} data-testid="setting-price-color"
                  onChange={(e) => set("card_price_color", e.target.value)}
                  className="w-10 h-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <span className="text-xs font-mono text-zinc-400">{form.card_price_color || "#241600"}</span>
              </div>
            </div>
            <div>
              <Label className="text-xs">Cantos do fundo do preço</Label>
              <Select value={String(form.card_price_radius ?? 2)} onValueChange={(v) => set("card_price_radius", parseFloat(v))}>
                <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="setting-price-radius"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  <SelectItem value="0">Quadrado</SelectItem>
                  <SelectItem value="1">Suave</SelectItem>
                  <SelectItem value="2">Arredondado</SelectItem>
                  <SelectItem value="4">Pill (bem redondo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <Button type="submit" disabled={saving} className="bg-gold text-black hover:bg-gold-light font-semibold px-10 h-11" data-testid="save-settings-button">
          {saving ? "Salvando..." : "Salvar configurações"}
        </Button>
      </form>
    </div>
  );
}
